import { useEffect, useMemo, useState, useCallback } from 'react'
import { useSharedTick } from '@/hooks/useSharedTick'
import { isDueNow, msUntilDue, todayISO } from './reminderTime'
import { wasSurfacedToday, markSurfaced, pruneLedger } from './surfacedLedger'
import { getPin, setPinned, setPosition as persistPosition, allPinnedIds } from './reminderPins'
import type { Reminder } from './types'

const MAX_AUTO = 2

export interface OnScreenNote {
  rem: Reminder
  pinned: boolean
  pos: { x: number; y: number } | null
}

/** Kept for unit tests: pure "which due, not-blocked reminders" (no longer the on-screen filter). */
export function selectPopups(items: Reminder[], tz: string, blocked: Set<string>, visible: boolean): Reminder[] {
  if (!visible) return []
  return items.filter((r) => isDueNow(r, tz) && !blocked.has(String(r.id))).slice(0, MAX_AUTO)
}

function isVisible(): boolean {
  return typeof document === 'undefined' ? true : document.visibilityState === 'visible'
}

/**
 * Drives popup surfacing. Returns onScreen notes + dismiss/togglePin/setPosition/noteResolved.
 *
 * Fix: "on screen" is explicit `autoShown` state — once added, a reminder stays until
 * explicitly removed (dismiss/done/togglePin-off). The surfaced ledger only gates ADDING.
 * This prevents the bug where re-renders cleared the popup immediately after surfacing.
 */
export function useReminderScheduler(items: Reminder[], tz: string) {
  const tick = useSharedTick(60000)
  const [visible, setVisible] = useState(isVisible)
  const [autoShown, setAutoShown] = useState<Set<string>>(new Set())
  // 按用户时区日期作用域:跨午夜后昨天的 dismiss 自动失效(spec §11 次日 overdue 要能再弹),
  // 长开页面不 reload 也不会永久压住提醒。写入端始终以"当下的今天"为键(见 addDismissed)。
  const [dismissedToday, setDismissedToday] = useState<{ date: string; ids: Set<string> }>(
    () => ({ date: todayISO(tz), ids: new Set() }),
  )
  const addDismissed = useCallback((id: string) => {
    const today = todayISO(tz)
    setDismissedToday((prev) => prev.date === today
      ? { date: today, ids: new Set(prev.ids).add(id) }
      : { date: today, ids: new Set([id]) })
  }, [tz])
  const [pinVersion, setPinVersion] = useState(0) // bump to re-read reminderPins (localStorage is not reactive)
  const [, force] = useState(0)
  const bumpPins = useCallback(() => setPinVersion((v) => v + 1), [])

  useEffect(() => {
    const onVis = () => { setVisible(isVisible()); force((x) => x + 1) }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onVis)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onVis)
    }
  }, [])

  // Precise timer to the nearest future due moment (unchanged behavior).
  useEffect(() => {
    const today = todayISO(tz)
    const now = new Date()
    let soonestMs = Infinity
    for (const r of items) {
      if (r.status !== 'open' || r.dueDate !== today || !r.dueTime || isDueNow(r, tz)) continue
      const delta = msUntilDue({ dueDate: r.dueDate, dueTime: r.dueTime }, tz, now)
      if (delta > 0 && delta < soonestMs) soonestMs = delta
    }
    if (soonestMs === Infinity) return
    const id = setTimeout(() => force((x) => x + 1), soonestMs + 250)
    return () => clearTimeout(id)
  }, [items, tz, tick])

  useEffect(() => { pruneLedger(todayISO(tz)) }, [tz, tick])

  // Effect: ADD newly-due reminders to autoShown (does NOT remove on re-render — that's the fix).
  //
  // ⚠️ 账本写 markSurfaced 必须在 setState updater 之外。React StrictMode(dev)会把
  // setState 的 updater 函数双调用一次:之前把 markSurfaced 写在 setAutoShown(prev => …)
  // 里 —— 第 1 次调用 add+写账本,第 2 次调用因账本已被自己污染(wasSurfacedToday=true)
  // 跳过该便签返回不含它的集合,而 React 采用第 2 次结果 → 便签被标记"今日已弹"却从未
  // 上屏,今天再也不弹(dev 实测复现:新建立即到期便签 → ledger +1 但 stickyPops 仍 0)。
  // 改法:effect 体内纯计算 next 集合 + 收集本次新上屏的 id,再在 updater 之外写账本,
  // 并用值形式 setAutoShown(next)(只在集合真的变化时提交,保持引用稳定不触发循环)。
  useEffect(() => {
    const today = todayISO(tz)
    const pinned = new Set(allPinnedIds())
    const next = new Set(autoShown)
    // Remove from autoShown if reminder is no longer eligible (done/deleted/became pinned),
    // or no longer due —— 从面板行/其它 tab snooze 挪到未来后,已弹的贴纸要跟着撤下
    // (snooze 会 clearSurfaced,新时刻到点仍能重新弹,不会被账本压住)
    for (const id of autoShown) {
      const r = items.find((x) => String(x.id) === id)
      if (!r || r.status !== 'open' || pinned.has(id) || !isDueNow(r, tz)) next.delete(id)
    }
    // Add newly-due reminders up to cap — 最早到期优先,不按数组顺序(新建项在数组最前)
    const newlySurfaced: string[] = []
    if (visible) {
      const byDue = [...items].sort((a, b) =>
        (a.dueDate + (a.dueTime ?? '')) < (b.dueDate + (b.dueTime ?? '')) ? -1 : 1)
      for (const r of byDue) {
        const id = String(r.id)
        if (next.size >= MAX_AUTO) break
        if (r.status !== 'open' || !isDueNow(r, tz)) continue
        const dismissed = dismissedToday.date === today && dismissedToday.ids.has(id)
        if (pinned.has(id) || next.has(id) || dismissed || wasSurfacedToday(id, today)) continue
        next.add(id)
        newlySurfaced.push(id)
      }
    }
    // Only commit when the set actually changed — keeps autoShown referentially stable so
    // this effect (which now depends on autoShown) doesn't loop.
    const sameSet = next.size === autoShown.size && [...next].every((id) => autoShown.has(id))
    if (!sameSet) {
      newlySurfaced.forEach((id) => markSurfaced(id, today))
      setAutoShown(next)
    }
  }, [items, tz, visible, tick, pinVersion, dismissedToday, autoShown])

  const onScreen: OnScreenNote[] = useMemo(() => {
    const pinned = new Set(allPinnedIds())
    const ids = new Set<string>([...pinned, ...autoShown])
    const out: OnScreenNote[] = []
    for (const id of ids) {
      const r = items.find((x) => String(x.id) === id)
      if (!r || r.status !== 'open') continue
      const p = getPin(id)
      let pos: { x: number; y: number } | null = null
      if (p !== undefined && p.x !== undefined && p.y !== undefined) {
        const px = p.x
        const py = p.y
        pos = { x: px, y: py }
      }
      out.push({ rem: r, pinned: pinned.has(id), pos })
    }
    return out.sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, autoShown, pinVersion, tick])

  const dismiss = useCallback((id: string) => {
    if (getPin(id)?.pinned) { setPinned(id, false); bumpPins() }
    markSurfaced(id, todayISO(tz))
    setAutoShown((prev) => { const n = new Set(prev); n.delete(id); return n })
    addDismissed(id)
  }, [tz, bumpPins, addDismissed])

  const togglePin = useCallback((id: string) => {
    const wasPinned = !!getPin(id)?.pinned
    if (wasPinned) {
      setPinned(id, false)
      markSurfaced(id, todayISO(tz))
      setAutoShown((prev) => { const n = new Set(prev); n.delete(id); return n })
      addDismissed(id)
    } else {
      setPinned(id, true)
    }
    bumpPins()
  }, [tz, bumpPins, addDismissed])

  const setPosition = useCallback((id: string, x: number, y: number) => {
    persistPosition(id, x, y)
    bumpPins()
  }, [bumpPins])

  const noteResolved = useCallback((id: string) => {
    if (getPin(id)?.pinned) { setPinned(id, false); bumpPins() }
    setAutoShown((prev) => { const n = new Set(prev); n.delete(id); return n })
  }, [bumpPins])

  return { onScreen, dismiss, togglePin, setPosition, noteResolved }
}
